/**
 * The Command Center's "why is this ticket with the front office?" — the
 * latest technician → FOH handoff, at the top of the default view, with the
 * same canonical Accept the FOH queue performs while it is still pending.
 *
 * Arriving from a notification (before acceptance) or from the queue (after
 * it) lands on the same context: nobody has to leave the ticket to find the
 * queue. Acceptance is the server's: the one custody gateway decides, a
 * concurrent accept is refused there, and the view re-reads custody either
 * way. Shown while the front office holds custody (FOH).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useTicketCustody, useTicketCustodyMutation } from '@/hooks/tickets/useTicketCustody';
import { useTicketCustodyHistory } from '@/hooks/tickets/useTicketCustodyHistory';
import { useCustodyActorNames } from '@/hooks/tickets/useCustodyActorNames';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissions } from '@/hooks/usePermissions';
import { custodyActorDisplay, latestMeaningfulHandoff } from '@/lib/tickets/lifecycle/custodyHistory';
import { TechnicianHandoffCard } from './TechnicianHandoffCard';

export function TicketHandoffContext({ ticketId }: { ticketId: string }) {
  const enabled = useFeatureFlag('custodyHandoff');
  const qc = useQueryClient();
  // The front-office roles — the people a technician hands a ticket to.
  const { canReassignAdvisor: isFrontOffice } = usePermissions();
  const { data: custody } = useTicketCustody(enabled ? ticketId : null);
  const withFoh = custody?.custody_owner === 'FOH';
  const { data: history } = useTicketCustodyHistory(ticketId, enabled && withFoh);
  const handoff = latestMeaningfulHandoff(history ?? [], ['kick_to_foh']);
  const { data: names } = useCustodyActorNames([handoff?.actorId]);
  const accept = useTicketCustodyMutation();

  if (!enabled || !withFoh) return null;

  const who = handoff ? custodyActorDisplay(handoff.actorId, handoff.actorRole, names) : null;
  const onAccept = isFrontOffice
    ? () => {
        accept
          .mutateAsync({ ticketId, eventKind: 'foh_accept', reason: null })
          .catch(() => {
            // Refused (e.g. someone else accepted first): the hook already
            // said so; converge on the server's custody instead of guessing.
            void qc.invalidateQueries({ queryKey: ['ticket-custody', ticketId] });
            void qc.invalidateQueries({ queryKey: ['ticket-custody-history', ticketId] });
          });
      }
    : undefined;

  return (
    <TechnicianHandoffCard
      handoff={handoff && who ? {
        actor: who.primary, actorRole: who.role, at: handoff.createdAt, reason: handoff.reason, notes: handoff.notes,
      } : null}
      custodyStatus={custody?.custody_status ?? null}
      onAccept={onAccept}
      accepting={accept.isPending}
    />
  );
}
